import type { FinanceEventRow, InventoryRow, OrdersMetrics, SellerConnection } from '../../api/amazonconnect';
import { amazonConnectApi } from '../../api/amazonconnect';
import { fetchInventorySample } from '../daily-brief/loadDailyBrief';
import type { DailyBriefModel } from '../daily-brief/types';
import { inventoryAvailable } from '../executive-kpis/buildKpis';
import { periodWindow } from '../executive-kpis/periodWindows';
import { fetchOrdersMetrics } from '../ordersTruth';
import type { ExecutiveKpiStripModel } from '../executive-kpis/types';
import type { MasterOverlayModel } from '../master-overlay/types';
import type { OpsIntelligenceModel } from '../ops-intelligence/types';
import type { DatePreset } from '../types';
import {
  fbaFacts,
  financeRollup,
  returnsFacts,
  settlementFacts,
  shipmentFacts,
  trafficFacts,
} from '../reportFacts';
import { fetchReportPages } from '../reportPages';

export type RestBlocksModel = {
  inventory: {
    available: number | null;
    inbound: number | null;
    zero: number | null;
    sampled: number;
    total: number | null;
    fbaFulfillable: number | null;
    fbaInbound: number | null;
    fbaRows: number;
    fbaTruncated: boolean;
    href: string;
  };
  finance: {
    count: number;
    total: number | null;
    events: FinanceEventRow[];
    refund: number;
    fee: number;
    other: number;
    settlementAmount: number | null;
    settlementRows: number;
    error: string | null;
    href: string;
  };
  traffic: {
    domain: string | null;
    quality: string | null;
    lastSync: string | null;
    records: number | null;
    sessions: number | null;
    pageViews: number | null;
    buyBoxPct: number | null;
    sample: number;
    truncated: boolean;
    href: string;
  };
  returns: {
    rows: number;
    sample: number;
    units: number | null;
    truncated: boolean;
    href: string;
  };
  fbaShipments: {
    rows: number;
    sample: number;
    truncated: boolean;
    href: string;
  };
  payment: {
    periodLabel: string;
    sales: number | null;
    currency?: string;
    codSales: number;
    prepaidSales: number;
    unclassifiedSales: number;
    codItems: number;
    prepaidItems: number;
    unclassifiedItems: number;
    classifiedOrders: number;
    orders: number;
  };
};

const REPORT_DOMAINS = [
  'returns',
  'fba_inventory',
  'sales_traffic',
  'settlement',
  'shipments',
] as const;

function emptyPayment(periodLabel: string): RestBlocksModel['payment'] {
  return {
    periodLabel,
    sales: null,
    codSales: 0,
    prepaidSales: 0,
    unclassifiedSales: 0,
    codItems: 0,
    prepaidItems: 0,
    unclassifiedItems: 0,
    classifiedOrders: 0,
    orders: 0,
  };
}

function paymentFromMetrics(periodLabel: string, m: OrdersMetrics): RestBlocksModel['payment'] {
  return {
    periodLabel,
    sales: m.sales,
    currency: m.currency,
    codSales: m.cod_sales ?? 0,
    prepaidSales: m.prepaid_sales ?? 0,
    unclassifiedSales: m.unclassified_sales ?? 0,
    codItems: m.cod_items ?? 0,
    prepaidItems: m.prepaid_items ?? 0,
    unclassifiedItems: m.unclassified_items ?? 0,
    classifiedOrders: m.classified_orders ?? 0,
    orders: m.orders ?? 0,
  };
}

export async function loadRestBlocks(opts: {
  connection: SellerConnection | null;
  brief?: DailyBriefModel | null;
  datePreset?: DatePreset;
}): Promise<RestBlocksModel> {
  const id = opts.connection?.id ?? null;
  const hrefInv = id ? `/amazon/inventory?connection=${encodeURIComponent(id)}` : '/amazon/inventory';
  const hrefFin = id ? `/amazon/finance?connection=${encodeURIComponent(id)}` : '/amazon/finance';
  const hrefConn = id ? `/amazon/connect?connection=${encodeURIComponent(id)}` : '/amazon/connect';

  const empty: RestBlocksModel = {
    inventory: {
      available: null,
      inbound: null,
      zero: null,
      sampled: 0,
      total: null,
      fbaFulfillable: null,
      fbaInbound: null,
      fbaRows: 0,
      fbaTruncated: false,
      href: hrefInv,
    },
    finance: {
      count: 0,
      total: null,
      events: [],
      refund: 0,
      fee: 0,
      other: 0,
      settlementAmount: null,
      settlementRows: 0,
      error: null,
      href: hrefFin,
    },
    traffic: {
      domain: null,
      quality: null,
      lastSync: null,
      records: null,
      sessions: null,
      pageViews: null,
      buyBoxPct: null,
      sample: 0,
      truncated: false,
      href: hrefConn,
    },
    returns: { rows: 0, sample: 0, units: null, truncated: false, href: hrefConn },
    fbaShipments: { rows: 0, sample: 0, truncated: false, href: hrefConn },
    payment: emptyPayment(''),
  };

  if (!id) return empty;

  const trafficRow = (opts.brief?.coverage.domains ?? []).find((d) =>
    /traffic|session|browse|sales_and_traffic|sales_traffic/i.test(d.key),
  );

  let inv = { rows: [] as InventoryRow[], total: null as number | null, zero: 0 };
  let events: FinanceEventRow[] = [];
  let financeTotal: number | null = null;
  let financeError: string | null = null;
  const window = periodWindow(opts.datePreset ?? '30d');
  let payment = emptyPayment(window.label);

  const [invRes, finRes, reports, metrics] = await Promise.all([
    fetchInventorySample(id).catch(() => inv),
    amazonConnectApi
      .listFinanceEventsPage(id, { page: 1, page_size: 8, sort: 'posted_date', order: 'desc' })
      .catch((e: unknown) => {
        financeError = e instanceof Error ? e.message : 'Finance events unavailable';
        return null;
      }),
    fetchReportPages(id, [...REPORT_DOMAINS]),
    fetchOrdersMetrics(id, { from: window.isoFrom, to: window.isoTo, tz: 'Asia/Kolkata' }).catch(() => null),
  ]);
  inv = invRes;
  if (finRes) {
    events = finRes.items ?? [];
    financeTotal = finRes.total_count ?? null;
  }
  if (metrics) payment = paymentFromMetrics(window.label, metrics);

  const roll = financeRollup(events);
  const fba = fbaFacts(reports.fba_inventory);
  const ret = returnsFacts(reports.returns);
  const traf = trafficFacts(reports.sales_traffic);
  const setl = settlementFacts(reports.settlement);
  const ship = shipmentFacts(reports.shipments);
  const avail = inventoryAvailable(inv.rows);
  const inbound =
    inv.rows.length === 0 ? null : inv.rows.reduce((s, r) => s + (Number(r.inbound) || 0), 0);

  return {
    inventory: {
      available: avail.sum,
      inbound,
      zero: inv.rows.length ? inv.zero : null,
      sampled: inv.rows.length,
      total: inv.total,
      fbaFulfillable: fba.fulfillable,
      fbaInbound: fba.inbound,
      fbaRows: fba.totalRows,
      fbaTruncated: fba.truncated,
      href: hrefInv,
    },
    finance: {
      count: events.length,
      total: financeTotal,
      events,
      refund: roll.refund,
      fee: roll.fee,
      other: roll.other,
      settlementAmount: setl.amount,
      settlementRows: setl.totalRows,
      error: financeError,
      href: hrefFin,
    },
    traffic: {
      domain: trafficRow?.key ?? (traf.totalRows > 0 ? 'sales_traffic' : null),
      quality: trafficRow?.quality ?? null,
      lastSync: trafficRow?.lastSync ?? null,
      records: trafficRow?.records ?? (traf.totalRows || null),
      sessions: traf.sessions,
      pageViews: traf.pageViews,
      buyBoxPct: traf.buyBoxPct,
      sample: traf.sample,
      truncated: traf.truncated,
      href: hrefConn,
    },
    returns: {
      rows: ret.totalRows,
      sample: ret.sample,
      units: ret.units,
      truncated: ret.truncated,
      href: hrefConn,
    },
    fbaShipments: {
      rows: ship.totalRows,
      sample: ship.sample,
      truncated: ship.truncated,
      href: hrefConn,
    },
    payment,
  };
}

export function buildSwot(ops: OpsIntelligenceModel | null, brief: DailyBriefModel | null) {
  const items = ops?.items?.length ? ops.items : [];
  const focus = brief?.focus ?? [];
  const strengths = items
    .filter((i) => i.priority === 'opportunity')
    .slice(0, 4)
    .map((i) => i.title);
  const risks = items
    .filter((i) => i.priority === 'critical' || i.priority === 'warning')
    .slice(0, 4)
    .map((i) => i.title);
  if (strengths.length === 0) {
    for (const f of focus) {
      if (f.severity === 'opportunity') strengths.push(f.title);
    }
  }
  if (risks.length === 0) {
    for (const f of focus) {
      if (f.severity === 'critical' || f.severity === 'warning') risks.push(f.title);
    }
  }
  return { strengths: strengths.slice(0, 4), risks: risks.slice(0, 4) };
}

export function buildSummary(brief: DailyBriefModel | null, kpis: ExecutiveKpiStripModel | null): string {
  const sales = brief?.metrics.find((m) => m.id === 'sales');
  const items = brief?.metrics.find((m) => m.id === 'orders');
  const units = brief?.metrics.find((m) => m.id === 'units');
  const gmv = kpis?.kpis.find((k) => k.id === 'gmv');
  const bits: string[] = [];
  if (sales?.value != null) {
    bits.push(
      `Yesterday sales ${Math.round(sales.value).toLocaleString('en-IN')} across ${items?.value ?? '—'} order items (${units?.value ?? '—'} units).`,
    );
  }
  if (gmv?.value != null && kpis) {
    bits.push(`${kpis.periodLabel} GMV ${Math.round(gmv.value).toLocaleString('en-IN')}.`);
  }
  if (brief?.health.overall != null) {
    bits.push(`Business health ${brief.health.overall} (${brief.health.band}).`);
  }
  if (bits.length === 0) return 'No sales figures on this connection yet.';
  return bits.join(' ');
}

export function timelineRows(overlay: MasterOverlayModel | null) {
  return (overlay?.points ?? []).slice(-14).map((p) => ({
    iso: p.iso,
    label: p.label,
    gmv: p.values.gmv ?? null,
    items: p.values.orders ?? null,
    units: p.values.units ?? null,
  }));
}
